<script lang="ts">
  // @ts-ignore
  import Global from "../components/global.svelte";
  import type { OpportunityDetailed } from "./types";

  export let opportunity: OpportunityDetailed | null = null;
  // export let retrying: boolean = false;
  export let title: string = "Details";
  export let labels: any = {};

  export let type: "opportunity" | "lead" | "generic" | "activity" =
    "opportunity";
  export let data: any = null;
  export let fields: Array<{
    label: string;
    key: string;
    format?: "datetime" | "badge" | "status" | "text";
  }> = [];
  export let config: {
    showRetry?: boolean;
    showBadge?: boolean;
    headerColor?: string;
  } = {};

  const defaultLabels = {
    awsId: "AWS ID",
    dealName: "Project Title",
    acelinkId: "Acelink ID",
    lastUpdate: "Last Update",
    awsSync: "AWS",
    acelinkSync: "Acelink",
    syncErrorTitle: "Sync Error",
    syncErrorMessage:
      "Sync validation failed. Please review the details and try again.",
    retryBtnText: "Sync",
    retryingBtnText: "Retrying...",
  };

  $: activeLabels = { ...defaultLabels, ...labels };
  $: displayData = data || opportunity;
  $: headerText =
    displayData?.name || displayData?.title || displayData?.label || title;
  $: currentStatus = displayData?.status || displayData?.state;
  $: displayFields =
    fields.length > 0
      ? fields
      : displayData
        ? Object.keys(displayData).map((k) => ({
            label:
              k.charAt(0).toUpperCase() + k.slice(1).replace(/([A-Z])/g, " $1"),
            key: k,
            format: "text" as const,
          }))
        : [];

  export let onClose: () => void = () => {};
  export let onSyncToAws: (
    opportunity: OpportunityDetailed,
  ) => Promise<void> = async () => {};
  export let onSyncToHubspot: (
    opportunity: OpportunityDetailed,
  ) => Promise<void> = async () => {};

  let syncLoadingAws = false;
  let syncLoadingHubspot = false;

  // Sync buttons are shown only when the opportunity is in AceLink
  $: if (displayData) {
    console.log("[OpportunityDrawer] sync flags:", {
      acelinkSynced: displayData.acelinkSynced,
      awsSynced: displayData.awsSynced,
      hubspotSynced: displayData.hubspotSynced,
      type,
      showAwsBtn:
        type === "opportunity" &&
        displayData.acelinkSynced &&
        !displayData.awsSynced,
      showHubspotBtn:
        type === "opportunity" &&
        displayData.acelinkSynced &&
        !displayData.hubspotSynced,
    });
  }

  const handleClose = (e?: Event) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log("Closing drawer...");
    onClose();
  };

  const handleSyncToAws = async () => {
    if (displayData) {
      syncLoadingAws = true;
      try {
        await onSyncToAws(displayData);
      } finally {
        syncLoadingAws = false;
      }
    }
  };

  const handleSyncToHubspot = async () => {
    if (displayData) {
      syncLoadingHubspot = true;
      try {
        await onSyncToHubspot(displayData);
      } finally {
        syncLoadingHubspot = false;
      }
    }
  };

  const formatTime = (isoString: string) => {
    if (!isoString) return "—";
    try {
      const date = new Date(isoString);
      return date.toLocaleString();
    } catch {
      return isoString;
    }
  };

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      handleClose();
    }
  };
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
<div
  class="fixed inset-0 bg-black/50 z-[1000] flex justify-end animate-[fadeIn_0.2s]"
  on:click={handleClose}
  on:keydown={handleKeydown}
  role="button"
  tabindex="0"
>
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    class="w-[500px] max-w-[90vw] bg-[#24283a] shadow-[-4px_0_20px_rgba(0,0,0,0.5)] flex flex-col animate-[slideIn_0.3s]"
    on:click|stopPropagation
    role="dialog"
  >
    <div
      class="flex justify-between items-center p-6 border-b border-white/10 bg-[#1b1e2e] gap-4"
      style={config.headerColor
        ? `background-color: ${config.headerColor}`
        : ""}
    >
      <div class="flex flex-row gap-4 flex-1 min-w-0">
        <h3
          class="text-[1.125rem] font-bold text-white m-0 overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {headerText}
        </h3>
        {#if type === "opportunity" || config.showBadge}
          <span
            class="inline-flex items-center px-3 py-1.5 rounded-2xl text-[0.813rem] font-semibold w-fit"
            class:badge-synced={currentStatus === "synced"}
            class:badge-pending={currentStatus === "not_synced" ||
              currentStatus === "pending"}
            class:badge-error={currentStatus === "error" ||
              currentStatus === "failed"}
          >
            {#if currentStatus === "synced"}
              🟢 Synced
            {:else if currentStatus === "not_synced" || currentStatus === "pending"}
              🟡 Pending
            {:else if currentStatus === "error" || currentStatus === "failed"}
              🔴 Error
            {:else}
              ⚪ {currentStatus || "Unknown"}
            {/if}
          </span>
        {/if}
      </div>
      <button
        class="bg-transparent border-none text-white/80 text-[1.5rem] cursor-pointer p-0 w-8 h-8 flex items-center justify-center rounded transition-all hover:bg-[#24283a]/10 hover:text-white"
        on:click|preventDefault|stopPropagation={(e) => handleClose(e)}
        aria-label="Close drawer"
      >
        ✕
      </button>
    </div>
    <div class="flex-1 overflow-y-auto p-6 flex flex-col gap-4">
      {#if displayData}
        <div
          class="bg-black/20 border border-white/10 rounded-lg overflow-hidden"
        >
          {#if type === "opportunity"}
            <div
              class="flex justify-between items-center px-5 py-4 border-b border-white/5 transition-colors hover:bg-[#24283a]/5"
            >
              <span class="text-white/60 text-sm font-semibold">
                {activeLabels.awsId}
              </span>
              <span class="text-white font-semibold text-[0.938rem] text-right">
                {displayData.awsOppId || "—"}
              </span>
            </div>

            <div
              class="flex justify-between items-center px-5 py-4 border-b border-white/5 transition-colors hover:bg-[#24283a]/5"
            >
              <span class="text-white/60 text-sm font-semibold">
                {activeLabels.dealName}
              </span>
              <span class="text-white font-semibold text-[0.938rem] text-right">
                {displayData.name || "—"}
              </span>
            </div>

            <div
              class="flex justify-between items-center px-5 py-4 border-b border-white/5 transition-colors hover:bg-[#24283a]/5"
            >
              <span class="text-white/60 text-sm font-semibold">
                {activeLabels.lastUpdate}
              </span>
              <span class="text-white font-semibold text-[0.938rem] text-right">
                {formatTime(displayData.lastUpdate)}
              </span>
            </div>

            <div class="h-px bg-[#24283a]/10 my-2" />

            <div
              class="flex justify-between items-center px-5 py-4 border-b border-white/5 transition-colors hover:bg-[#24283a]/5"
            >
              <span class="text-white/60 text-sm font-semibold">
                {activeLabels.awsSync}
              </span>
              <div
                class={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold ${
                  displayData.awsSynced
                    ? "bg-green-500/10 border border-green-500/30"
                    : "bg-red-500/10 border border-red-500/30"
                }`}
              >
                {#if displayData.awsSynced}
                  <span class="text-green-500 text-base font-bold">✓</span>
                  <span class="text-green-500">Synced</span>
                {:else}
                  <span class="text-red-500 text-base font-bold">✕</span>
                  <span class="text-red-500">Not Synced</span>
                {/if}
              </div>
            </div>

            <div
              class="flex justify-between items-center px-5 py-4 border-b border-white/5 transition-colors hover:bg-[#24283a]/5"
            >
              <span class="text-white/60 text-sm font-semibold"> HubSpot </span>
              <div
                class={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold ${
                  displayData.hubspotSynced
                    ? "bg-green-500/10 border border-green-500/30"
                    : "bg-red-500/10 border border-red-500/30"
                }`}
              >
                {#if displayData.hubspotSynced}
                  <span class="text-green-500 text-base font-bold">✓</span>
                  <span class="text-green-500">Synced</span>
                {:else}
                  <span class="text-red-500 text-base font-bold">✕</span>
                  <span class="text-red-500">Not Synced</span>
                {/if}
              </div>
            </div>

            <div
              class="flex justify-between items-center px-5 py-4 border-b border-white/5 transition-colors hover:bg-[#24283a]/5"
            >
              <span class="text-white/60 text-sm font-semibold">
                {activeLabels.acelinkSync}
              </span>
              <div
                class={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold ${
                  displayData.acelinkSynced
                    ? "bg-green-500/10 border border-green-500/30"
                    : "bg-red-500/10 border border-red-500/30"
                }`}
              >
                {#if displayData.acelinkSynced}
                  <span class="text-green-500 text-base font-bold">✓</span>
                  <span class="text-green-500">In Acelink</span>
                {:else}
                  <span class="text-red-500 text-base font-bold">✕</span>
                  <span class="text-red-500">Not in Acelink</span>
                {/if}
              </div>
            </div>

            {#if displayData.recommendation}
              <div
                class="px-5 py-4 border-b border-white/5 transition-colors hover:bg-[#24283a]/5"
              >
                <span class="block text-white/60 text-sm font-semibold mb-2">
                  Recommendation
                </span>
                <p class="text-white/90 text-sm leading-relaxed whitespace-pre-wrap m-0">
                  {displayData.recommendation}
                </p>
              </div>
            {/if}
          {:else if type === "generic" || type === "lead" || type === "activity"}
            {#each displayFields as field}
              {#if displayData[field.key] !== undefined && field.key !== "name" && field.key !== "id"}
                <div
                  class="flex justify-between items-center px-5 py-4 border-b border-white/5 transition-colors hover:bg-[#24283a]/5"
                >
                  <span class="text-white/60 text-sm font-semibold">
                    {field.label}
                  </span>
                  <span
                    class="text-white font-semibold text-[0.938rem] text-right"
                  >
                    {#if field.format === "datetime"}
                      {formatTime(displayData[field.key])}
                    {:else if field.format === "badge"}
                      <span class="px-2 py-1 rounded-md bg-[#24283a]/10 text-xs">
                        {displayData[field.key]}
                      </span>
                    {:else}
                      {displayData[field.key] || "—"}
                    {/if}
                  </span>
                </div>
              {/if}
            {/each}
          {/if}
        </div>

        <slot name="extra-info"></slot>

        {#if displayData.status === "error" || displayData.syncError}
          <div class="bg-red-500/10 border border-red-500/30 rounded-lg p-5">
            <div class="flex items-start gap-4">
              <span class="text-[1.5rem] flex-shrink-0">⚠️</span>
              <div>
                <h4 class="text-[0.938rem] font-bold text-white mb-2">
                  {activeLabels.syncErrorTitle}
                </h4>
                <p class="text-white/85 m-0 text-sm leading-relaxed">
                  {displayData.syncErrorMessage ||
                    activeLabels.syncErrorMessage}
                </p>
              </div>
            </div>
          </div>
        {/if}

        {#if type === "opportunity" && displayData.acelinkSynced}
          <div class="flex gap-2">
            <!-- Always show both buttons when opportunity is in Acelink -->
            <button
              class="w-full px-5 py-3 rounded-lg font-semibold text-[0.938rem] flex items-center justify-center gap-2 bg-[#1b1e2e] text-white border border-white/20 shadow-[0_2px_8px_rgba(0,0,0,0.3)] disabled:opacity-60 disabled:cursor-not-allowed hover:bg-[#3b3b54] hover:border-white/50 hover:scale-[1.02] hover:shadow-[0_4px_12px_rgba(0,0,0,0.4),0_0_8px_rgba(255,255,255,0.1)] transition-all duration-200 active:scale-[0.98]"
              on:click={handleSyncToAws}
              disabled={syncLoadingAws || syncLoadingHubspot}
            >
              {#if syncLoadingAws}
                Syncing to AWS...
              {:else}
                Sync to AWS
              {/if}
            </button>

            <button
              class="w-full px-5 py-3 rounded-lg font-semibold text-[0.938rem] flex items-center justify-center gap-2 bg-[#1b1e2e] text-white border border-white/20 shadow-[0_2px_8px_rgba(0,0,0,0.3)] disabled:opacity-60 disabled:cursor-not-allowed hover:bg-[#3b3b54] hover:border-white/50 hover:scale-[1.02] hover:shadow-[0_4px_12px_rgba(0,0,0,0.4),0_0_8px_rgba(255,255,255,0.1)] transition-all duration-200 active:scale-[0.98]"
              on:click={handleSyncToHubspot}
              disabled={syncLoadingAws || syncLoadingHubspot}
            >
              {#if syncLoadingHubspot}
                Syncing to HubSpot...
              {:else}
                Sync to HubSpot
              {/if}
            </button>
          </div>
        {/if}
      {:else}
        <div class="text-center py-12 text-white/50">
          <p>Loading details...</p>
        </div>
      {/if}
    </div>
  </div>
</div>
